import React from 'react';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import Tooltip from '@mui/material/Tooltip';
import Badge from '@mui/material/Badge';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import useVoyagerStore from '../../stores/useVoyagerStore';
import { TreeNode } from '../../types';
import getTreeNodeIcon from './getTreeNodeIcon';
import { qbDimensionInk } from '../VoyagerPanes/queryBuilderPresentation';

export default function TreeNodeItem({ node, depth = 0 }: { node: TreeNode; depth?: number }) {
  const expandedNodes = useVoyagerStore((s) => s.expandedNodes);
  const focusedNodeId = useVoyagerStore((s) => s.focusedNodeId);
  const selectedMetrics = useVoyagerStore((s) => s.selectedMetrics);
  const selectedDimensions = useVoyagerStore((s) => s.selectedDimensions);
  const toggleMetric = useVoyagerStore((s) => s.toggleMetric);
  const toggleDimension = useVoyagerStore((s) => s.toggleDimension);
  const semanticModel = useVoyagerStore((s) => s.semanticModel);

  const isExpanded = expandedNodes.has(node.id);
  const isFocused = focusedNodeId === node.id;
  const hasChildren = node.children && node.children.length > 0;

  const nodeName = node.metadata?.name;
  const instanceCount = node.type === 'metric'
    ? selectedMetrics.filter((m) => m.metricName === nodeName).length
    : 0;
  const isSelected = node.type === 'metric'
    ? instanceCount > 0
    : (node.type === 'dimension' || node.type === 'date_hierarchy')
      ? selectedDimensions.includes(nodeName)
      : false;

  const isActionable = node.type === 'metric' || node.type === 'dimension';

  const handleToggleExpand = () => {
    const store = useVoyagerStore.getState();
    const newExpanded = new Set(store.expandedNodes);
    if (isExpanded) {
      newExpanded.delete(node.id);
    } else {
      newExpanded.add(node.id);
    }
    useVoyagerStore.setState({ expandedNodes: newExpanded });
  };

  const handleSelect = () => {
    if (node.type === 'metric' && nodeName) {
      toggleMetric(nodeName);
    } else if (node.type === 'date_hierarchy' && nodeName) {
      toggleDimension(nodeName);
    } else if (node.type === 'dimension' && nodeName) {
      toggleDimension(nodeName);
    } else if (hasChildren) {
      handleToggleExpand();
    }
  };


  const descriptionText = node.metadata?.description || '';

  return (
    <>
      <ListItem
        dense
        onClick={handleSelect}
        sx={{
          pl: 1 + depth * 1.75,
          pr: 1,
          py: 0.375,
          my: '1px',
          borderRadius: '7px',
          cursor: 'pointer',
          bgcolor: isSelected ? 'var(--accent-soft)' : isFocused ? 'var(--sunk)' : 'transparent',
          '&:hover': { bgcolor: isSelected ? 'var(--accent-soft)' : 'var(--sunk)' },
          borderLeft: '3px solid',
          borderLeftColor: isSelected ? 'var(--accent)' : 'transparent',
        }}
      >
        {hasChildren ? (
          <ListItemIcon sx={{ minWidth: 28 }} onClick={(e) => { e.stopPropagation(); handleToggleExpand(); }}>
            {isExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
          </ListItemIcon>
        ) : (
          <ListItemIcon sx={{ minWidth: 28 }}>
            {instanceCount > 1 ? (
              <Badge badgeContent={instanceCount} color="primary" sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', minWidth: 14, height: 14 } }}>
                {getTreeNodeIcon(node)}
              </Badge>
            ) : (
              getTreeNodeIcon(node)
            )}
          </ListItemIcon>
        )}
        {hasChildren && (
          <ListItemIcon sx={{ minWidth: 24 }}>
            {getTreeNodeIcon(node)}
          </ListItemIcon>
        )}
        <ListItemText
          primary={node.label}
          primaryTypographyProps={{
            variant: 'body2',
            fontWeight: node.type === 'domain' || node.type === 'group' || node.type === 'inherited_model' ? 600 : 400,
            color: node.type === 'metric' ? 'var(--accent-ink)'
              : (node.type === 'dimension' || node.type === 'date_hierarchy') ? qbDimensionInk
              : 'text.primary',
            noWrap: true,
            fontSize: node.type === 'domain' ? 14 : 13,
          }}
        />
        {isActionable && descriptionText && (
          <Tooltip title={descriptionText} placement="right" arrow enterDelay={400}>
            <InfoOutlinedIcon sx={{ fontSize: 14, ml: 0.5, opacity: 0.5 }} />
          </Tooltip>
        )}
      </ListItem>

      {isExpanded && hasChildren && (
        <List disablePadding>
          {node.children!.map((child) => (
            <TreeNodeItem key={child.id} node={child} depth={depth + 1} />
          ))}
        </List>
      )}
    </>
  );
}
